import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { Button, Input, Checkbox, Icon } from '../components/ui';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, isAuthenticated, getDefaultRoute } = useUser();
  const navigate = useNavigate();

  // If already logged in, redirect to dashboard
  if (isAuthenticated) {
    return <Navigate to={getDefaultRoute()} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const success = await login(email, password);
      if (success) {
        // Small delay to ensure auth state is updated
        setTimeout(() => {
          navigate(getDefaultRoute());
        }, 100);
      } else {
        setError('Invalid email or password');
      }
    } catch {
      setError('An error occurred during login');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      // dvh minus the notch: body already pads by --sat, so 100vh would leave the
      // installed app scrolling a few px and jumping when the keyboard opens
      className="min-h-[calc(100dvh-var(--sat))] flex items-center justify-center p-4"
      style={{ background: 'var(--surface-canvas)' }}
    >
      <div
        className="w-full p-6 md:p-[32px]"
        style={{
          maxWidth: '400px',
          background: 'var(--surface-card)',
          border: '1px solid var(--border-hairline)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-pop)',
        }}
      >
        <div className="ck-stagger">
          {/* Mark + title */}
          <div className="flex flex-col items-center" style={{ marginBottom: 'var(--space-8)' }}>
            <img
              src="/bullfinch-mark.svg"
              alt="Bullfinch"
              style={{ width: '44px', height: '44px', marginBottom: 'var(--space-4)' }}
            />
            <h1
              style={{
                margin: 0,
                fontSize: 'var(--text-xl)',
                fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                letterSpacing: 'var(--tracking-tight)',
                color: 'var(--text-body)',
              }}
            >
              Cockpit
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
              Sign in to continue
            </p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col" style={{ gap: 'var(--space-4)' }}>
            {error && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '9px 12px',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--status-down-soft)',
                  border: '1px solid rgba(229, 72, 77, 0.25)',
                  color: 'var(--danger-500)',
                  fontSize: 'var(--text-sm)',
                }}
              >
                <Icon name="circle-alert" size={15} />
                {error}
              </div>
            )}

            <Input
              label="Email"
              icon="mail"
              type="email"
              fieldSize="lg"
              // iOS keychain autofill + no auto-capitalised first letter
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@bullfinch.io"
              required
            />

            <Input
              label="Password"
              icon="lock"
              type="password"
              fieldSize="lg"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              minLength={6}
            />

            <Checkbox
              checked={rememberMe}
              onChange={setRememberMe}
              label={
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
                  Remember me
                </span>
              }
            />

            <Button
              type="submit"
              size="lg"
              disabled={isLoading}
              style={{ width: '100%', marginTop: 'var(--space-2)' }}
            >
              {isLoading ? (
                <span className="flex items-center" style={{ gap: '8px' }}>
                  <span
                    className="animate-spin"
                    style={{
                      width: '14px',
                      height: '14px',
                      borderRadius: 'var(--radius-pill)',
                      border: '2px solid rgba(255, 247, 244, 0.35)',
                      borderTopColor: 'var(--text-on-accent)',
                      display: 'inline-block',
                    }}
                  />
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
